// Hooks de React Query para o domínio Client.
// - useClientsInfinite — listagem paginada por cursor (DR-032).
// - useClient — detalhe.
// - useCreateClient / useUpdateClient (optimistic update) / useCancelClient.
//
// Convenção: queryKey ['clients', 'list', filters] e ['clients', 'detail', id].
// Após qualquer mutation: invalidateQueries({ queryKey: ['clients'] }).

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import {
  cancelClient,
  createClient,
  fetchClient,
  fetchClients,
  resetClientEmailInvalid,
  updateClient,
  type ClientEntity,
  type ClientListParams,
  type ClientListResponse,
} from '../lib/clients-api';
import type { CreateClientDto, UpdateClientDto } from '@control-center/shared';

const DEFAULT_LIMIT = 20;

export const clientsKeys = {
  all: ['clients'] as const,
  lists: () => [...clientsKeys.all, 'list'] as const,
  list: (params: Omit<ClientListParams, 'cursor'>) => [...clientsKeys.lists(), params] as const,
  details: () => [...clientsKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientsKeys.details(), id] as const,
};

export type ClientsListFilters = Omit<ClientListParams, 'cursor' | 'limit'>;

export function useClientsInfinite(filters: ClientsListFilters = {}) {
  return useInfiniteQuery<
    ClientListResponse,
    Error,
    InfiniteData<ClientListResponse>,
    ReturnType<typeof clientsKeys.list>,
    string | undefined
  >({
    queryKey: clientsKeys.list({ ...filters, limit: DEFAULT_LIMIT }),
    queryFn: ({ pageParam, signal }) =>
      fetchClients({ ...filters, limit: DEFAULT_LIMIT, cursor: pageParam }, signal),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useClient(id: string | undefined) {
  return useQuery<ClientEntity, Error>({
    queryKey: id ? clientsKeys.detail(id) : ['clients', 'detail', 'noop'],
    queryFn: ({ signal }) => fetchClient(id as string, signal),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/**
 * Story 3.7 — busca single-page pra autocomplete (TransferModal).
 * Diferente do useClientsInfinite: retorna apenas a primeira página,
 * sem paginação. Adequado pra dropdown de seleção.
 */
export function useClientsSearch(filters: ClientsListFilters = {}, limit = 20) {
  return useQuery<ClientListResponse, Error>({
    queryKey: ['clients', 'search', { ...filters, limit }],
    queryFn: ({ signal }) => fetchClients({ ...filters, limit }, signal),
    staleTime: 30_000,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation<ClientEntity, Error, CreateClientDto>({
    mutationFn: (payload) => createClient(payload),
    onSuccess: (client) => {
      qc.setQueryData(clientsKeys.detail(client.id), client);
      qc.invalidateQueries({ queryKey: clientsKeys.lists() });
    },
  });
}

export interface UpdateClientArgs {
  id: string;
  payload: UpdateClientDto;
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation<ClientEntity, Error, UpdateClientArgs, { previous?: ClientEntity }>({
    mutationFn: ({ id, payload }) => updateClient(id, payload),
    onMutate: async ({ id, payload }) => {
      await qc.cancelQueries({ queryKey: clientsKeys.detail(id) });
      const previous = qc.getQueryData<ClientEntity>(clientsKeys.detail(id));
      if (previous) {
        qc.setQueryData<ClientEntity>(clientsKeys.detail(id), {
          ...previous,
          ...payload,
          // mantém id/version originais — backend confirma o novo version no response
          id: previous.id,
          version: previous.version,
        });
      }
      return { previous };
    },
    onError: (_err, { id }, context) => {
      if (context?.previous) {
        qc.setQueryData(clientsKeys.detail(id), context.previous);
      }
    },
    onSettled: (client, _err, { id }) => {
      if (client) {
        qc.setQueryData(clientsKeys.detail(id), client);
      }
      qc.invalidateQueries({ queryKey: clientsKeys.lists() });
      qc.invalidateQueries({ queryKey: clientsKeys.detail(id) });
    },
  });
}

export interface CancelClientArgs {
  id: string;
  version: number;
}

export function useCancelClient() {
  const qc = useQueryClient();
  return useMutation<ClientEntity, Error, CancelClientArgs>({
    mutationFn: ({ id, version }) => cancelClient(id, version),
    onSuccess: (client) => {
      qc.setQueryData(clientsKeys.detail(client.id), client);
      qc.invalidateQueries({ queryKey: clientsKeys.lists() });
    },
  });
}

/**
 * Story 6.10 — limpa flag email-invalid de um cliente. Admin-only.
 * Após sucesso, invalida cache detail pra recarregar `emailInvalidAt = null`.
 */
export function useResetClientEmailInvalid() {
  const qc = useQueryClient();
  return useMutation<{ clientId: string; reset: true }, Error, string>({
    mutationFn: (clientId) => resetClientEmailInvalid(clientId),
    onSuccess: (_result, clientId) => {
      qc.invalidateQueries({ queryKey: clientsKeys.detail(clientId) });
    },
  });
}
